import React, { useMemo, useState, useEffect, useRef, useContext } from 'react';
import dynamic from 'next/dynamic';
import posthog from 'posthog-js';
import Cookies from 'js-cookie';
import { Element as ScrollElement } from 'react-scroll';
import { useRouter } from 'next/router';
import { useStore } from '../../store';
import { addProduct, showCart } from '../../store/store.actions';
import { productMOQ, ProductStage, ProductTag, CartEventLocation } from '../../types/common';
import { CookieConsent } from '../../types/cookies';
import {
  analyticsTrackAddToCart,
  analyticsTrackItemSelectedFromList,
  analyticsTrackViewCart,
  isUserLoggedIn,
  trackSubscribeButtonClicked,
  getSubscribeModalLocation,
} from '../../utils/analytics';
import {
  getStage,
  hasSale,
  parseMetafields,
  hasProductTag,
  getProductIDFromShopifyGID,
  isProductSoldOut,
  shouldShowLowStockMessage,
} from '../../utils/product';
import { getCreatorDropDetailsByProduct } from '../../utils/collections';
import ProductCard from '../../ui/organisms/ProductCard';
import config from '../../../config.json';
import SpamFlaggingContext from '../../context/spamFlagging';
import { isHybridPreOrder, isReadyToShip } from '../pages/products/commonFunctions';
import {
  getProductCardSpecs,
  getBadgeSpecs,
  getProductImageSrc,
  getProductSubtitle,
  getCtaText,
  getOnCtaClick,
  getModalData,
  fetchProductTotalSold,
} from './ProductCardHelpers';

const LazySubscribeModal = dynamic(() => import('../Modal/SubscribeModal'), { ssr: false });

type ProductCardContainerProps = {
  product: Shopify.Product;
  titleForTracking?: string;
  query?: string;
};

const ProductCardContainer: React.FC<ProductCardContainerProps> = ({ product, titleForTracking, query }) => {
  const { state, dispatch } = useStore();
  const { incrementSpamCounter, isSpam } = useContext(SpamFlaggingContext);
  const { asPath } = useRouter();
  const cardRef = useRef<HTMLDivElement>(null);

  const [totalSold, setTotalSold] = useState<number>(0);
  const [hasAchievedStretchGoals, setHasAchievedStretchGoals] = useState(false);
  const [shouldEnableStretchGoals, setShouldEnableStretchGoals] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalCtaText, setModalCtaText] = useState<string>('Subscribe');

  const metafields = parseMetafields(product.metafields);
  const stage = getStage(product.tags);
  const sale = hasSale(product);
  const isLimited = hasProductTag(product.tags, ProductTag.Limited);
  const isPetition = hasProductTag(product.tags, ProductTag.Petition);
  const isSuccessfulPetitionNotLive = stage === ProductStage.PetitionSuccess;
  const isLiveWithStretchGoalsTag = hasProductTag(product.tags, ProductTag.StretchGoals) && stage === ProductStage.Live;
  const moq = Number(metafields.moq) || productMOQ[product.productType] || config.defaultMOQ;
  const isSoldOut = isProductSoldOut(product, totalSold, stage);
  const hideUnits = hasProductTag(product.tags, ProductTag.HideUnits);
  const shouldShowFreeGiftBadge = isLiveWithStretchGoalsTag && shouldEnableStretchGoals;

  // Calculate shipping availability
  const showLowStock = shouldShowLowStockMessage(metafields.lowStockThreshold, totalSold);
  const showReadyToShip = !isSoldOut && isReadyToShip(product) && !showLowStock;
  const showPreOrder = isHybridPreOrder(product);

  const handleVisible = async (product: Shopify.Product) => {
    const totalSold = await fetchProductTotalSold(product, isPetition, isSuccessfulPetitionNotLive);
    setTotalSold(totalSold);
  };

  useEffect(() => {
    const currentRef = cardRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleVisible(product);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '20%',
      },
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [product]);

  useEffect(() => {
    if (isLiveWithStretchGoalsTag) {
      (async () => {
        const { shouldEnableStretchGoals, goalsAchieved } = await getCreatorDropDetailsByProduct(product);
        setShouldEnableStretchGoals(shouldEnableStretchGoals);
        setHasAchievedStretchGoals(goalsAchieved);
      })();
    }
  }, [isLiveWithStretchGoalsTag, product]);

  const specs = useMemo(
    () =>
      getProductCardSpecs({
        product,
        totalSold,
        stage,
        metafields,
        isLimited,
        isPetition,
        isSuccessfulPetitionNotLive,
        moq,
        isSoldOut,
        hideUnits,
        showLowStock,
        showReadyToShip,
        showPreOrder,
      }),
    [
      product,
      totalSold,
      stage,
      metafields,
      isLimited,
      isPetition,
      isSuccessfulPetitionNotLive,
      moq,
      isSoldOut,
      hideUnits,
      showLowStock,
      showReadyToShip,
      showPreOrder,
    ],
  );

  const handleClick = () => {
    // Store visited product card for scroll restoration
    const cookieConsentAccepted = Cookies.get(CookieConsent.Functional) === 'true';
    if (cookieConsentAccepted) {
      sessionStorage.setItem('visitedProductCard', product.title);
    }

    // Track product card click
    posthog.capture('product_card_click', {
      product_title: product.title,
      product_id: getProductIDFromShopifyGID(product.id),
      product_stage: stage,
      product_tags: product.tags,
      location: asPath,
      product_type: product.productType,
    });

    analyticsTrackItemSelectedFromList(product, titleForTracking, state.user);

    // Track search result clicks with search query
    if (titleForTracking === 'search') {
      posthog.capture('search_result_click', {
        product_title: product.title,
        product_id: getProductIDFromShopifyGID(product.id),
        search_query: query?.trim(),
        location: 'search_results_page',
        product_stage: stage,
        product_tags: product.tags,
        is_logged_in: isUserLoggedIn(state.user),
        result_type: 'Product',
      });
    }
  };

  const handleCtaClick = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const variant = product.variants.edges[0]?.node;
    if (!variant) return;

    addProduct(state.cart, 1, variant).then((action) => dispatch(action));
    dispatch(showCart());
    incrementSpamCounter();

    if (!isSpam) {
      analyticsTrackAddToCart(product, 1, state.user, CartEventLocation.ProductThumbnail, variant);
      analyticsTrackViewCart(
        state.cart.shopifyCart?.lines.edges,
        state.cart.shopifyCart?.cost.subtotalAmount.amount,
        state.user,
        CartEventLocation.ProductThumbnail,
      );
    }
  };

  const handleSubscribeClick = (ctaText: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setModalCtaText(ctaText);
    setIsModalOpen(true);
    trackSubscribeButtonClicked(product, state.user, ctaText, getSubscribeModalLocation());
  };

  const modalData = useMemo(
    () => getModalData(modalCtaText, product.title, metafields.creator || 'the creator'),
    [modalCtaText, product.title, metafields.creator],
  );

  return (
    <div ref={cardRef} className="overflow-hidden">
      <ScrollElement name={product.title} />
      <ProductCard
        title={product.title}
        subtitle={getProductSubtitle(metafields)}
        imageSrc={getProductImageSrc(product)}
        href={`/products/${product.handle}`}
        specs={specs}
        onClick={handleClick}
        ctaText={getCtaText(stage, product)}
        badgeSpecs={getBadgeSpecs(sale, shouldShowFreeGiftBadge, hasAchievedStretchGoals)}
        onCtaClick={getOnCtaClick(
          stage,
          product,
          isSoldOut,
          isPetition,
          handleCtaClick,
          handleSubscribeClick,
          handleClick,
        )}
      />
      <LazySubscribeModal
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        tags={modalData.tags}
        description={modalData.description}
        title={modalData.title}
        ctaText={modalCtaText}
        product={product}
        eventLocation={getSubscribeModalLocation()}
      />
    </div>
  );
};

export default ProductCardContainer;
